import moment from 'moment';
import 'moment-timezone';
import generateUuid from '../utils/generateUuid';

export default class StatusQueue {
	constructor(
		{
			interval,
			maxStatusAge,
			maxStatusNumber,
			component,
			checkEndDate,
			onStatusRemoved,
			onQueueEmpty,
			nonExpiring,
		},
		initialStatuses
	) {
		this.queue = [
			...(initialStatuses || []).map((s) => ({
				...s,
				id: generateUuid(),
				moment: moment(s.date),
				endDate: s.endDate ? moment(s.endDate) : null,
			})),
		];
		this.cycleTimeout = null;
		this.cycling = false;
		this.currentStatus = null;
		this.hadItems = Boolean(this.queue.length);
		this.component = component;
		this.interval = interval;
		this.maxStatusAge = maxStatusAge;
		this.maxStatusNumber = maxStatusNumber;
		this.checkEndDate = checkEndDate;
		this.onQueueEmpty = onQueueEmpty;
		this.onStatusRemoved = onStatusRemoved;
		this.nonExpiring = nonExpiring;
		this.prepareInitialQueue();
	}

	get count() {
		return this.queue.length;
	}

	get hasItems() {
		return Boolean(this.queue.length);
	}

	prepareInitialQueue() {
		this.queue.sort(function(a, b) {
			if (a.moment.isBefore(b.moment)) {
				return 1;
			}
			if (a.moment.isAfter(b.moment)) {
				return -1;
			}
			return 0;
		});

		if (this.queue.length > this.maxStatusNumber) {
			this.queue = this.queue.slice(0, this.maxStatusNumber);
		}
	}

	start() {
		this.cycling = true;
		this.cycle(true);
	}

	addStatus(status) {
		this.queue.unshift({
			...status,
			id: generateUuid(),
			moment: status.startDate ? moment(status.startDate) : moment(),
			endDate: status.endDate ? moment(status.endDate) : null,
		});

		this.hadItems = true;

		// remove statuses above limit
		if (this.queue.length > this.maxStatusNumber) {
			this.queue = this.queue.slice(0, this.maxStatusNumber);
		}

		if (this.cycling) {
			this.cycle(true);
		}
	}

	removeStatus(statusId) {
		const statusIndex = this.queue.findIndex((s) => s.uuid === statusId);
		const statusLastIndex = this.queue.findLastIndex(
			(s) => s.uuid === statusId
		);
		if (statusIndex >= 0) {
			const status = this.queue[statusIndex];
			this.queue.splice(statusIndex, statusLastIndex - statusIndex + 1);
			this.onStatusRemoved?.(statusId);
			if (this.currentStatus.uuid === status.uuid && this.cycling) {
				this.cycle();
			}

			if (!this.queue.length && this.hadItems && this.onQueueEmpty) {
				this.hadItems = false;
				this.onQueueEmpty();
			}
		}
	}

	stop() {
		this.cycling = false;
		this.cleanup();
	}

	cycle(useFirst) {
		this.cleanup();
		const nextStatus = useFirst ? this.queue[0] : this.getNextStatus();
		if (nextStatus && nextStatus.id !== this.currentStatus?.id) {
			this.currentStatus = nextStatus;
			this.component.showStatus(nextStatus);
		} else if (this.currentStatus) {
			this.component.updateActiveMoment(this.currentStatus);
		}

		if (this.cycling) {
			this.cycleTimeout = setTimeout(this.cycle.bind(this), this.interval);
		}
	}

	getNextStatus() {
		let nextIndex = 0;

		if (this.currentStatus) {
			const indexOfCurrent = this.queue.findIndex(
				(v) => v.id === this.currentStatus.id
			);
			if (indexOfCurrent < this.queue.length - 1) {
				nextIndex = indexOfCurrent + 1;
			}
		}

		return this.queue[nextIndex];
	}

	cleanup() {
		clearTimeout(this.cycleTimeout);

		// clean items past their end date
		if (this.checkEndDate) {
			this.queue = this.queue.filter((s) => {
				const shouldStay = !s.endDate || !moment().isAfter(s.endDate);
				if (!shouldStay) {
					this.onStatusRemoved?.(s.uuid);
				}
				return shouldStay;
			});
		}

		// if there are multiple, remove statuses that are too old
		if (this.queue.length > 1 && !this.nonExpiring) {
			const cutoffMoment = moment().subtract(this.maxStatusAge);

			const indexOfFirstOlder = this.queue.findIndex((s) =>
				s.moment.isBefore(cutoffMoment)
			);
			if (indexOfFirstOlder >= 0) {
				for (let i = indexOfFirstOlder; i < this.queue.length; i++) {
					const element = this.queue[i];
					this.onStatusRemoved?.(element.uuid);
				}
				this.queue = this.queue.slice(0, indexOfFirstOlder);
			}
		}

		if (!this.hasItems && this.hadItems && this.onQueueEmpty) {
			this.hadItems = false;
			this.onQueueEmpty();
		}
	}
}
